/**
 * Organization admin page — landing for admins to manage org-wide settings.
 * Currently exposes two tabs:
 *   - Usuarios  — same UI as the standalone /users page
 *   - Gestionar UI — branding / theme settings preview
 * Servidores MCP used to be a third tab here — it's now its own sidebar
 * entry + pages (McpServersListPage / McpServerDetailPage), same pattern as
 * Tokens LLM, since it needed its own list→detail navigation.
 */
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tabs, Spin } from 'antd';
import { TeamOutlined, BgColorsOutlined, BankOutlined, ApartmentOutlined } from '@ant-design/icons';
import AppPageLayout from './common/AppPageLayout';
import PageHeader from './common/PageHeader';
import { useAdminGuard } from '../hooks/useAdminGuard';
import { useUser } from '../contexts/UserContext';
import UsersAndTeamsPanel from './organization/UsersAndTeamsPanel';
import UIManagementPanel from './organization/UIManagementPanel';
import OrganizationsManagementPanel from './organization/OrganizationsManagementPanel';

const OrganizationPage: React.FC = () => {
  const { loading } = useAdminGuard();
  const { isSuperadmin } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'users';

  if (loading) {
    return (
      <AppPageLayout>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
          <Spin size="large" />
        </div>
      </AppPageLayout>
    );
  }

  return (
    <AppPageLayout>
      <PageHeader
        icon={<BankOutlined />}
        title="Administrar Organización"
        subtitle="Configuración y gestión de la organización."
      />

      <Tabs
        activeKey={tab}
        onChange={(key) => setSearchParams({ tab: key })}
        items={[
          {
            key: 'users',
            label: <span><TeamOutlined /> Usuarios y Equipos</span>,
            children: <UsersAndTeamsPanel />,
          },
          {
            key: 'ui',
            label: <span><BgColorsOutlined /> Gestionar UI</span>,
            children: <UIManagementPanel />,
          },
          ...(isSuperadmin ? [{
            key: 'organizations',
            label: <span><ApartmentOutlined /> Organizaciones</span>,
            children: <OrganizationsManagementPanel />,
          }] : []),
        ]}
      />
    </AppPageLayout>
  );
};

export default OrganizationPage;
